// MealPreferencesGrid.jsx
import React, { useEffect, useState } from "react";
import MealPreferencesExportButton from "./MealPreferencesExportButton";

const API = process.env.REACT_APP_API_URL;
const DAYS = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
const DAY_LABELS = {
  monday: "Montag",
  tuesday: "Dienstag",
  wednesday: "Mittwoch",
  thursday: "Donnerstag",
  friday: "Freitag",
  saturday: "Samstag",
  sunday: "Sonntag",
};

export default function MealPreferencesGrid({ className = "" }) {
  const [data, setData] = useState({ patients: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await fetch(`${API}/mealpreferences`, {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData({ patients: json.patients || [], count: json.count || 0 });
      } catch (e) {
        if (!cancelled) setErr(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const exportDisabled = loading || !!err || !data.patients?.length;

  return (
    <section className={className}>
      {/* Export button (now a separate component) */}
      <div className="flex justify-end mb-2">
        <MealPreferencesExportButton
          patients={data.patients}
          disabled={exportDisabled}
        />
      </div>

      <div>
        {loading && (
          <div className="py-8 text-sm text-gray-600">Loading meal preferences…</div>
        )}

        {err && !loading && (
          <div className="py-6 text-sm text-red-600">Error: {err}</div>
        )}

        {!loading && !err && data.patients.length === 0 && (
          <div className="py-8 text-sm text-gray-600">Keine Patienten gefunden.</div>
        )}

        {!loading && !err && data.patients.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full border-collapse bg-white/70">
              <thead>
                <tr className="bg-[#76B3FF] text-white">
                  <th className="whitespace-nowrap px-3 py-2 text-left text-sm font-medium">
                    Patient
                  </th>
                  {DAYS.map((d) => (
                    <th
                      key={d}
                      className="whitespace-nowrap px-3 py-2 text-left text-sm font-medium"
                    >
                      {DAY_LABELS[d]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.patients.map((p, i) => (
                  <tr
                    key={p.id}
                    className={i % 2 === 0 ? "bg-white" : "bg-white/60"}
                  >
                    <td className="px-3 py-2 text-sm font-semibold text-gray-900">
                      {p.name || `Patient ${p.id}`}
                    </td>

                    {DAYS.map((d) => {
                      const val = p.preferences?.[d] ?? "none";
                      const isNone = !val || String(val).toLowerCase() === "none";
                      return (
                        <td key={d} className="px-3 py-2 text-sm">
                          <span
                            className={[
                              "inline-flex items-center rounded-full px-2 py-0.5",
                              "border text-xs",
                              isNone
                                ? "border-slate-200 bg-slate-50 text-slate-500"
                                : "border-emerald-200 bg-emerald-50 text-emerald-700",
                            ].join(" ")}
                            title={isNone ? "Keine Präferenz" : val}
                          >
                            {isNone ? "—" : val}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}